import { useEffect, useState } from 'react';
import type { PlaybackResponse, VideoStatus } from '@ddp/shared';

import { apiFetch } from '../../api/client.js';

/**
 * Résout l'URL pré-signée de l'**image de couverture** (`cover.jpg`) produite par le worker au
 * transcodage. On la sert comme un simple JPEG : un `<img>` s'affiche quel que soit le codec de la
 * source, là où peindre la source elle-même échoue silencieusement sur un MP4 non « faststart »
 * (atome `moov` en fin de fichier) ou un HEVC — le cas courant des sources drone sur Safari iOS.
 *
 * La couverture n'existe qu'une fois la vidéo `READY` (même job que le rendu HLS, ADR-0007) : avant,
 * on renvoie `null` et l'appelant montre un repli neutre « en traitement ». L'octet ne traverse pas
 * l'API (§2) : on récupère une URL de stockage à durée de vie courte, jamais persistée (NFR-5).
 */
export function useVideoCover(videoId: string, status: VideoStatus, enabled = true): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || status !== 'READY') {
      setUrl(null);
      return;
    }
    let active = true;
    apiFetch<PlaybackResponse>(`/videos/${videoId}/playback`)
      .then((playback) => {
        if (active) setUrl(playback.coverUrl);
      })
      .catch(() => {
        if (active) setUrl(null);
      });
    return () => {
      active = false;
    };
  }, [videoId, status, enabled]);

  return url;
}
